import { all, dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Divider from 'tio-ui/components/divider';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { flatten, safeParse } from 'valibot';
import { fn } from '@ember/helper';
import { formatDateToISO } from 'tio-common/utils/date';
import Input from 'tio-ui/components/input';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { on } from '@ember/modifier';
import { and, eq, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { setSemesterCodeString } from 'tio-common/utils/format';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import {
  copyFieldsAndUpdatePerProgramTemplate,
  getAllValuesForTasField,
  getConfigForTasField,
  getSingleValueForTasField,
  setSingleValueForTasField,
  getFieldLabelForFieldName,
  type FieldValue,
  type TemplateFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { capitalizeWords } from 'tio-employee/utils/tas/textUtilities';
import { Header, HStack, Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { join, not } from 'tio-ui/utilities';
import { trackedObject } from '@ember/reactive/collections';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import ActionableList from 'tio-ui/components/actionable-list';
import AddEducationDrawer from 'tio-employee/components/tas/add-education-drawer';
import AddScholarshipsDrawer from 'tio-common/components/tas/add-scholarships-drawer';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import { divide } from 'tio-ui/math';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import LimitsSummary from 'tio-common/components/tuition-assistance/limits-summary';
import ReimbursementCalculationIfApplicable from 'tio-common/components/tuition-assistance/reimbursement-calculation-if-applicable';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import AlertBanner from 'tio-ui/components/alert-banner';
import ErrorMessages from 'tio-ui/components/error-messages';
import TuitionAssistanceFormsCustomFieldsForProvince from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import TourTrigger from 'tio-common/components/tours/tour-trigger';
import type {
  CustomFieldSignature,
  ReceivedScholarship,
} from 'tio-common/types/tuition-assistance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { Touched } from 'tio-common/helpers/errors-for-valibot-field';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TASProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import { deleteExistingRecord, reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import {
  abandonCourseRequest,
  buildTasTransitionFieldData,
  transitionTasApplicationRequest,
  TAS_TRANSITION_EVENT_FOR_METHOD,
} from 'tio-data/requests/tas-application-actions';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import ReimbursementPopoverTag from 'tio-common/components/tas/reimbursement-popover-tag';
import {
  PhPencil,
  PhCheckCircle,
  PhCircle,
  PhLightbulb,
  PhNotePencil,
  PhXCircle,
  PhCaretLeft,
  PhTrash,
  PhClipboardText,
  PhPlus,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import PaymentMethodAndSubtotals from '../../payment-method-and-subtotals';
import type PartnerService from 'tio-employee/services/partner';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type TourManager from 'tio-common/services/tour-manager';
import { normalizeDate } from 'tio-common/utils/date/normalize-date';
import { courseCashApplicationPage } from 'tio-common/utils/tours/course-cash-tour';
import { hash } from '@ember/helper';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';

type ProgramInstanceFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FormField = {
  name: ProgramInstanceFieldName;
  mask: string | null;
  type: string | null;
  rules: {
    type: string;
    typeError?: string;
  };
};

export interface NewTypeFourSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      programInstance: TASProgramInstanceModel;
    };
  };
}

interface Scholarship extends ReceivedScholarship {
  id?: string;
}

export default class NewTypeFourComponent extends Component<NewTypeFourSignature> {
  programTemplate: TasProgramTemplateModel;

  constructor(owner: Owner, args: NewTypeFourSignature['Args']) {
    super(owner, args);
    const { programInstance, application } = args.model;
    this.programTemplate = programInstance.tasProgramTemplate;

    // Set default payment method BEFORE creating trackedObject to avoid tracking errors
    if (this.programTemplate.isDirectPayProgram) {
      const existingMethod = getSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        application.fields,
        ''
      );
      if (!existingMethod) {
        setSingleValueForTasField(
          'PAYMENT_TYPE_FOR_DIRECT_PAY',
          'TAS.AssistanceManner.COURSE_CASH',
          application.fields
        );
      }
    }

    if (this.showGPAField) {
      this.setGPA(getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || '0');
    }
    this.applicationFields = trackedObject(application.fields);
    this.updateEligibility();
    this.scholarshipToDelete = null;
    const customFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      application.customFields,
      this.programTemplate.customFields
    );

    this.applicationCustomFields = customFieldsCopy.filter(
      (field) => field.province === 'APPLICATION'
    );
    this.updateCustomRequiredFields();
    this.initializePaymentMethod();
    this.endDate = getSingleValueForTasField('COURSES_END_DATE', this.applicationFields);
    this.startDate = getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields);
    this.initializeLimitsIfNeeded();
    this.registerTours();
    if (!!this.startDate) {
      this.touched = { dateEntered: true };
    } else {
      this.touched = { dateEntered: false };
    }
  }

  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked course: TasCourseModel | null = null;
  @tracked eligibility = false;
  @tracked hasSubmitted = false;
  @tracked isDeleteApplicationOpen = false;
  @tracked isDeleteCourseOpen = false;
  @tracked isDeleteScholarshipOpen = false;
  @tracked isEducationOpen = false;
  @tracked isOpen = false;
  @tracked isScholarshipOpen = false;
  @tracked scholarshipToDelete: Scholarship | null;
  @tracked submitError: string | null = null;
  @tracked gpaBasedAmount: number = 0;
  @tracked requiredCustomFieldsFilled = false;
  @tracked highlightedSection: string | null = null;
  @tracked paymentMethod = 'TAS.AssistanceManner.REIMBURSEMENT';
  @tracked showPaymentMethodModal = false;
  @tracked endDate = '';
  @tracked startDate = '';
  @tracked touched: Touched = { dateEntered: false };
  @tracked shouldFetchLimits = false;
  @tracked scholarshipIdToEdit?: string;
  applicationFields!: TasApplicationModel['fields'];
  applicationCustomFields: CustomFieldSignature[] = [];

  @tracked dynamicEligibility?: boolean;
  @tracked programEligibility: [string, string][] = [];

  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare partner: PartnerService;
  @service declare tourManager: TourManager;
  @service declare betaExperience: BetaExperienceService;

  private registerTours() {
    if (this.programTemplate.isDirectPayProgram) {
      // pass the function to open the education drawer to the tour steps
      this.tourManager.registerTour(courseCashApplicationPage(this.openEducationDrawer));
    }
  }

  initializeLimitsIfNeeded() {
    const basedOn = this.programTemplate.calculateTowardsLimitBasedOn;
    const hasSetDate =
      (basedOn === 'COURSE_START_DATE' && !!this.startDate) ||
      (basedOn === 'COURSE_END_DATE' && !!this.endDate);

    if (!hasSetDate) return;

    this.shouldFetchLimits = true;
  }

  calculateEligibility() {
    if (!this.programEligibility.length) {
      return false;
    }
    // Use today's date for 'submission' lock status, otherwise use the provided start or end date.
    const enteredDate =
      this.lockStatus === 'submission' ? new Date() : new Date(this.startOrEndDate);

    return this.programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  async updateEligibility(): Promise<void> {
    // Don't check eligibility if the application is in ignore policies mode
    if (
      this.application.ignorePolicies ||
      this.lockStatus === 'fulfilled' ||
      !this.lockStatus ||
      !this.application.applicationSubmittedDate
    ) {
      this.eligibility = true;
      return;
    }
    try {
      if (!this.dynamicEligibility || !this.programEligibility.length) {
        const id = this.instance.employee.tasParticipant.id as string;
        const { code } = this.programTemplate;

        const [dynamicResponse, periodsResponse] = await Promise.all([
          this.store.request(dynamicEligibilityRequest(id)),
          this.store.request(periodsOfEligibilityRequest(id)),
        ]);
        // Legacy adapter filtered the per-program-code value off the raw response.
        const dynamicEligibility = dynamicResponse.content as DynamicEligibility;
        const programEligibility = periodsResponse.content as PeriodOfEligibility;

        this.dynamicEligibility = dynamicEligibility[code];
        this.programEligibility = programEligibility[code] as [string, string][];
      }

      this.eligibility = this.dynamicEligibility! && this.calculateEligibility();
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      this.eligibility = false;
    }
  }

  get application() {
    return this.args.model.application;
  }

  get instance() {
    return this.args.model.programInstance;
  }

  get applicationName() {
    return getSingleValueForTasField('APPLICATION_NAME', this.applicationFields);
  }

  get educationSectionTitle() {
    return this.application.expenseType || 'Education';
  }

  get typeClassification() {
    return this.programTemplate.typeClassification || '';
  }

  get conditionsForSubmit() {
    return this.programTemplate.typeTwoApplicationConditions || [];
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return saveRecord(this.store, agreement);
    });
    const saved = await all(promises);
    return saved;
  });

  get shouldShowAttachments() {
    if (this.typeClassification === 'TAS.ProgramType.4.2') {
      return (
        this.programTemplate.attachmentsForCourseApprovalVisible ||
        this.programTemplate.attachmentsForCourseCompletionVisible
      );
    }
    return this.programTemplate.attachmentsForCourseApprovalVisible;
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    if (this.typeClassification === 'TAS.ProgramType.4.2') {
      return (
        this.programTemplate.requireAttachmentsForCourseApproval ||
        this.programTemplate.requireAttachmentsForCourseCompletion
      );
    }
    return this.programTemplate.requireAttachmentsForCourseApproval;
  }

  get attachmentClassificationString() {
    return this.typeClassification === 'TAS.ProgramType.4.2'
      ? 'COMPLETION_DOCUMENTS'
      : 'COURSE_APPROVAL_DOCUMENTS';
  }

  get attachmentsListItems() {
    const { coursePreApproval, courseCompletion } = this.programTemplate.attachmentListItems;

    const itemsToInclude =
      this.typeClassification === 'TAS.ProgramType.4.2'
        ? [coursePreApproval, courseCompletion]
        : [coursePreApproval];

    return itemsToInclude.flat();
  }

  get attachmentsListInstructions() {
    const { coursePreApproval, courseCompletion } = this.programTemplate.attachmentInstructions;

    const itemsToInclude =
      this.typeClassification === 'TAS.ProgramType.4.2'
        ? [coursePreApproval, courseCompletion].filter(Boolean)
        : [coursePreApproval].filter(Boolean);

    return itemsToInclude.length ? itemsToInclude.join(', ') : '';
  }

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn;
  }

  get lockStatus() {
    const lockType: Record<string, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]
      : '';
  }

  get expenseTypeField() {
    let expenseType = getSingleValueForTasField(
      'EXPENSE_TYPE',
      this.programTemplate.fields
    ) as string;
    return expenseType || 'Education';
  }

  get scholarships() {
    let savedScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.application.fields
    );
    return savedScholarships.map((scholarship) => {
      const s = scholarship as ReceivedScholarship;
      return {
        ...s,
        id: s.scholarshipId,
      };
    });
  }

  get scholarshipName() {
    return this.programTemplate.fields['SCHOLARSHIP_NAME'];
  }

  get scholarshipAmount() {
    return this.programTemplate.fields['SCHOLARSHIP_AMOUNT'];
  }

  get startDateLabel() {
    return getFieldLabelForFieldName('COURSES_BEGIN_DATE', this.programTemplate.fields) as string;
  }

  get endDateLabel() {
    return getFieldLabelForFieldName('COURSES_END_DATE', this.programTemplate.fields) as string;
  }

  get scholarshipReceived() {
    return this.programTemplate.fields['SCHOLARSHIPS_RECEIVED'];
  }

  get eligibleBasedOnWaitingPeriod() {
    return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant,
      this.startDate || undefined
    );
  }

  get eligibilityWaitingPeriod() {
    return this.programTemplate.eligibilityWaitingPeriod as number;
  }

  get dateEligible() {
    const date = this.tuitionAssistance.waitingPeriodEndDate(
      this.eligibilityWaitingPeriod,
      this.instance.employee?.tasParticipant
    );
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get startDateValidationErrors() {
    const startDateConfig = getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
    if (!startDateConfig.visible) {
      return {};
    }
    const schema = this.tuitionAssistance.dynamicBeginDateValidationSchema(
      this.programTemplate,
      this.endDate
    );
    const model = {
      dateEntered: new Date(this.startDate),
    };

    const result = safeParse(schema, model, { abortEarly: false });
    if (!result.success) {
      const flattened = flatten(result.issues);
      return flattened.nested ?? {};
    }
    return {};
  }

  get hasStartDateValidationErrors(): boolean {
    const errors = this.startDateValidationErrors;
    return Object.keys(errors || {}).length > 0;
  }

  get isActiveParticipant() {
    return this.instance.employee?.tasParticipant?.isActive || false;
  }

  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  updateCustomRequiredFields = () => {
    let customFields = [...this.applicationCustomFields];

    this.requiredCustomFieldsFilled = customFields.every((field) => {
      if (field.required) {
        return field.values[0] !== null && field.values[0] !== undefined && field.values[0] !== '';
      }
      return true;
    });
  };

  didUpdateCustomFields = (customFields: CustomFieldSignature[]) => {
    this.applicationCustomFields = customFields;
    this.updateCustomRequiredFields();
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  setSemesterCodeStringOnApplication = () => {
    if (this.programTemplate.semesterCodeIsVisible) {
      const date = getSingleValueForTasField(
        'COURSES_BEGIN_DATE',
        this.applicationFields
      ) as string;
      if (!date) {
        return;
      }
      const semesterCode = setSemesterCodeString(date);
      setSingleValueForTasField('SEMESTER_CODE', semesterCode, this.applicationFields);
    }
  };

  updateStartDate = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.saveStartDate(isoDate);
    } else {
      this.saveStartDate('');
    }
  };

  saveStartDate = async (value: string) => {
    this.startDate = normalizeDate(value as string) || '';
    setSingleValueForTasField('COURSES_BEGIN_DATE', this.startDate, this.applicationFields);
    this.touched = { dateEntered: true };

    if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_START_DATE') {
      this.application.fields = this.applicationFields;
      await saveRecord(this.store, this.application);
      this.shouldFetchLimits = true;

      if (!this.startDateValidationErrors.length) {
        // Refresh the limits status data when the start date changes
        this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
      }
    }
    if (this.lockEmployeeStatusBasedOn === 'COURSE_START_DATE') {
      await this.updateEligibility();
    }
  };

  updateEndDate = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.saveEndDate(isoDate);
    } else {
      this.saveEndDate('');
    }
  };

  saveEndDate = async (value: string) => {
    this.endDate = normalizeDate(value as string) || '';
    setSingleValueForTasField('COURSES_END_DATE', this.endDate, this.applicationFields);

    if (this.programTemplate.calculateTowardsLimitBasedOn === 'COURSE_END_DATE') {
      this.application.fields = this.applicationFields;
      await saveRecord(this.store, this.application);
      this.shouldFetchLimits = true;
      // Refresh the limits status data when the end date changes
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    }

    if (this.lockEmployeeStatusBasedOn === 'COURSE_END_DATE') {
      await this.updateEligibility();
    }
  };

  updateAppName = (value: string) => {
    setSingleValueForTasField('APPLICATION_NAME', value, this.applicationFields);
  };

  scrollTo = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.highlightedSection = sectionId;
    setTimeout(() => (this.highlightedSection = null), 2000);
  };

  openEducationDrawer = (course?: TasCourseModel | null) => {
    if (course) {
      this.course = course;
    } else {
      this.course = this.store.createRecord('tas-course', {
        tasApplication: this.application,
        fields: this.programTemplate.courseFieldsHash,
        customFields: this.programTemplate.courseCustomFields,
      });
    }
    this.isEducationOpen = true;
  };

  closeEducationDrawer = () => {
    this.course = null;
    this.isEducationOpen = false;
  };

  toggleScholarshipDrawer = () => {
    this.isScholarshipOpen = !this.isScholarshipOpen;
    if (!this.isScholarshipOpen) {
      this.scholarshipIdToEdit = undefined;
      // Sync local applicationFields with any scholarship changes saved in the drawer
      this.syncScholarshipsFromApplication();
    }
  };

  syncScholarshipsFromApplication() {
    // Update the local trackedObject with the latest scholarship data from application.fields
    if (this.application.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFields['SCHOLARSHIPS_RECEIVED'] =
        this.application.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  get requiredFieldsSet() {
    const fieldsToCheck: TemplateFieldName[] = [
      'APPLICATION_NAME',
      'COURSES_BEGIN_DATE',
      'COURSES_END_DATE',
    ];

    return fieldsToCheck.every((field) => {
      const fieldConfig = getConfigForTasField(
        field as TemplateFieldName,
        this.programTemplate.fields
      );

      if (!fieldConfig.required) {
        return true; // Skips non-required
      }
      const flatFields = { ...this.applicationFields };
      const value = getSingleValueForTasField(field, flatFields);

      return value !== null && value !== undefined && value !== '';
    });
  }

  get hasRequiredAttachments() {
    let hasRequiredAttachments: boolean = false;
    switch (this.typeClassification) {
      case 'TAS.ProgramType.4.2':
        hasRequiredAttachments =
          this.application.hasRequiredEvidenceAttachments ||
          this.application.hasRequiredPreApprovalAttachments;
        break;
      case 'TAS.ProgramType.4.3':
        hasRequiredAttachments = this.application.hasRequiredPreApprovalAttachments;
        break;
      default:
        console.error(`Unhandled typeClassification: ${this.typeClassification}`);
        hasRequiredAttachments = false;
    }
    return hasRequiredAttachments;
  }

  get canSubmitApplication() {
    const requiredState = 'TAS.ApplicationState.DEFINE_COURSES';

    return (
      this.isActiveParticipant &&
      !!this.application.activeCourses.length &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      this.application.state === requiredState &&
      this.requiredFieldsSet &&
      this.hasGpaIfRequired &&
      this.requiredCustomFieldsFilled &&
      this.eligibleBasedOnWaitingPeriod &&
      !this.hasStartDateValidationErrors
    );
  }

  get startOrEndDate(): string {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.applicationFields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.applicationFields);
    }
    return new Date().toLocaleDateString('en-CA');
  }

  get startDateConfig() {
    return getConfigForTasField('COURSES_BEGIN_DATE', this.programTemplate.fields);
  }

  get endDateConfig() {
    return getConfigForTasField('COURSES_END_DATE', this.programTemplate.fields);
  }

  get isClaimsFinancialsApplication() {
    return this.programTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get setRequestedTotalAmount() {
    if (this.isClaimsFinancialsApplication) {
      return 0;
    }

    if (!this.calculateTotalByGPA) {
      return this.application.requestedTotal;
    }
    return Math.min(this.gpaBasedAmount, this.application.requestedTotal);
  }

  get hasGpaIfRequired(): boolean {
    // checks if the GPA field is required
    if (!this.showGPAField) {
      return true;
    }

    const hasField = Boolean(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.applicationFields)
    );

    return hasField;
  }

  get showGPAField() {
    return this.calculateTotalByGPA && this.typeClassification === 'TAS.ProgramType.4.2';
  }

  get approvedTotalString() {
    if (this.typeClassification === 'TAS.ProgramType.4.2') {
      return 'FINAL_APPROVED_TOTAL';
    } else {
      return 'PRE_APPROVED_TOTAL';
    }
  }

  setGPA = (value: string) => {
    const gpa = Number(value);
    setSingleValueForTasField('GRADE_POINT_AVERAGE', gpa, this.applicationFields);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
  };

  submitApplication = async () => {
    if (!this.canSubmitApplication) {
      return;
    }
    try {
      await this.prepareTotalsMiscFieldsAndSave();
      await this.saveAgreementsForConditions.perform();
      await this.submitTask.perform();
    } catch (error) {
      console.error('Failed to submit application:', error);
    }
  };

  get adapterMethod() {
    const actionMap = {
      'TAS.ProgramType.4.2': 'requestCourseEvidence',
      'TAS.ProgramType.4.3': 'requestCoursesApproval',
    };
    return actionMap[this.typeClassification as keyof typeof actionMap] || '';
  }

  async prepareTotalsMiscFieldsAndSave() {
    this.setSemesterCodeStringOnApplication();

    // Save payment method for direct pay programs
    if (this.programTemplate.isDirectPayProgram) {
      setSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        this.paymentMethod,
        this.applicationFields
      );
    }

    if (
      this.programTemplate.isDirectPayProgram &&
      this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
    ) {
      this.setSubtotals();
    } else {
      setSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', null, this.applicationFields);
      setSingleValueForTasField(
        this.approvedTotalString,
        this.setRequestedTotalAmount,
        this.applicationFields
      );
    }

    await this.saveTask.perform();
  }

  submitTask = dropTask(async () => {
    this.hasSubmitted = true;
    this.submitError = null;
    try {
      await this.prepareTotalsMiscFieldsAndSave();
      const limitsValidationResponse = await this.validateLimits.perform();
      if (!limitsValidationResponse.can_submit) {
        this.displayLimitsValidationError(limitsValidationResponse);
        return;
      }
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter[this.adapterMethod](application) (triggerActionAndReload →
      // request-courses-approval / request-evidence-approval transition + reload).
      const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.adapterMethod];
      if (!event) {
        throw new Error(
          `No TAS application adapter method available for typeClassification="${this.typeClassification}"`
        );
      }
      await this.store.request(
        transitionTasApplicationRequest(
          this.application.id as string,
          event,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole),
          '',
          buildTasTransitionFieldData(this.application.fields)
        )
      );
      // TAS application state transitions go through
      // StateTransitionableController#transition → `head 204` (renders NO
      // resource), so the builder does NOT refresh the cache — keep the reload
      // via the helper.
      await reloadRecord(this.store, this.application);

      this.router.transitionTo('authenticated.tas.confirmation', {
        queryParams: { tasType: 'four', applicationId: this.application.id },
      });
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  validateLimits = dropTask(async () => {
    const results = await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
      this.args.model.application,
      this.args.model.application.tasProgramInstance.employee
    );
    return results;
  });

  displayLimitsValidationError(validationResponse = {}) {
    // TODO: Eventually this message will be configurable via the program template.
    const reductionDisplay = this.application.isCreditBased
      ? // @ts-expect-error: not sure about this one
        `${validationResponse.reduction_needed} credits`
      : // @ts-expect-error: not sure about this one
        this.formatMoney(validationResponse.reduction_needed);
    this.submitError = `You have exceeded your employer's maximum limit. Please reduce your requested amount by ${reductionDisplay}.`;
  }

  formatMoney(cents: number) {
    const dollars = cents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  saveTask = dropTask(async () => {
    this.application.fields = this.applicationFields;
    this.application.customFields = this.applicationCustomFields;
    try {
      await saveRecord(this.store, this.application);
    } catch (error) {
      console.error('Failed to save courses or application:', error);
    }
  });

  switchToGuidedFlow = dropTask(async () => {
    try {
      await this.saveTask.perform();
      if (this.application.hasDirtyAttributes) {
        return;
      }
    } catch {
      return;
    }
    this.betaExperience.setProductEnabled('ta', true);
    this.router.transitionTo(
      'authenticated.tas.applications.type-four-guided.new',
      this.application.id
    );
  });

  onTrash = () => {
    this.isDeleteApplicationOpen = true;
  };

  toggleDeleteApplication = () => {
    this.isDeleteApplicationOpen = !this.isDeleteApplicationOpen;
  };

  toggleDeleteCourse = (course: TasCourseModel | null) => {
    this.isDeleteCourseOpen = !this.isDeleteCourseOpen;
    if (course) {
      this.course = course;
    }
  };

  deleteApplication = dropTask(async () => {
    try {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter.abandonCourse(application) (triggerActionAndReload → abandon transition + reload).
      await this.store.request(
        abandonCourseRequest(
          this.application.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole),
          '',
          buildTasTransitionFieldData(this.application.fields)
        )
      );
      // `abandon` goes through StateTransitionableController#transition →
      // `head 204` (renders NO resource), so the builder does NOT refresh the
      // cache — keep the reload via the helper.
      await reloadRecord(this.store, this.application);
      // TAS.TODO: temporary routing until tas dashboard is complete
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error(e);
    }
  });

  deleteCourse = dropTask(async () => {
    if (!this.course) {
      return;
    }
    try {
      if (this.course.isNew) {
        await this.course.rollbackAttributes();
      } else {
        await deleteExistingRecord(this.store, this.course);
      }
      this.isDeleteCourseOpen = false;
      this.course = null;
    } catch (e) {
      console.error(e);
    }
  });

  // TAS.TODO: need custom field validation
  get customFieldValidationErrors() {
    return [];
  }

  openDeleteScholarship = (scholarship: ReceivedScholarship) => {
    this.scholarshipToDelete = scholarship;
    this.isDeleteScholarshipOpen = true;
  };

  closeDeleteScholarship = () => {
    this.scholarshipToDelete = null;
    this.isDeleteScholarshipOpen = false;
  };

  deleteScholarship = () => {
    let existingScholarships = getAllValuesForTasField(
      'SCHOLARSHIPS_RECEIVED',
      this.args.model.application.fields
    );
    let updatedScholarships = existingScholarships.filter(
      (scholarship: FieldValue) =>
        (scholarship as Record<string, unknown>)?.scholarshipId !==
        this.scholarshipToDelete?.scholarshipId
    ) as ReceivedScholarship[];
    this.args.model.application.fields['SCHOLARSHIPS_RECEIVED'] = {
      values: updatedScholarships.filter((scholarship) => scholarship !== null),
    };
    void saveRecord(this.store, this.args.model.application);
    this.isDeleteScholarshipOpen = false;
  };

  openEditScholarship = (scholarship: ReceivedScholarship) => {
    this.scholarshipIdToEdit = scholarship.scholarshipId;
    this.isScholarshipOpen = true;
  };

  getValue = (values: string[] | boolean[]): string => {
    return values[0]?.toString() || '';
  };

  setPaymentMethod = (method: string) => {
    this.paymentMethod = method;

    setSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.paymentMethod,
      this.applicationFields
    );
  };

  closePaymentMethodModal = () => {
    this.showPaymentMethodModal = false;
    // Save the application to persist the payment method change
    this.saveTask.perform();
  };

  initializePaymentMethod() {
    if (!this.programTemplate.isDirectPayProgram) return;

    const method = getSingleValueForTasField(
      'PAYMENT_TYPE_FOR_DIRECT_PAY',
      this.applicationFields,
      ''
    );
    // Set the tracked property for UI display (default was already set in constructor)
    this.paymentMethod = method || 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isTypeFourDotTwoProgram() {
    return this.typeClassification === 'TAS.ProgramType.4.2';
  }

  setSubtotals() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.application;

    const courseCashTotalField = this.isTypeFourDotTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = this.isTypeFourDotTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setSingleValueForTasField(
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships,
      this.applicationFields
    );
    setSingleValueForTasField(
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal,
      this.applicationFields
    );
  }

  inputClasses = { base: 'w-full mb-6' };

  <template>
    <TourTrigger @tourId="course-cash-application" @autoStart={{true}} @delay={{500}} />
    <VStack>
      <Header class="tio-row-between">
        <div>
          <LinkTo class="tio-row-loose" @route="authenticated.tas.dashboard">
            <PhCaretLeft @size="16" />
            {{t "dashboard"}}
          </LinkTo>
        </div>
        <div class="tio-row-loose">
          <button
            type="button"
            class="text-xs tio-fg-muted underline hover:tio-fg-secondary cursor-pointer"
            data-test-id="standard-flow-toggle"
            disabled={{this.switchToGuidedFlow.isRunning}}
            {{on "click" this.switchToGuidedFlow.perform}}
          >
            {{t "tas.guided.try_guided_view"}}
          </button>
          <Button
            data-test-save-application
            @intent="primary"
            @appearance="outlined"
            @isRunning={{this.saveTask.isRunning}}
            {{on "click" this.saveTask.perform}}
          >
            {{t "common.save"}}
          </Button>
          <Button
            @intent="danger"
            @appearance="outlined"
            data-test-delete-application
            {{on "click" this.onTrash}}
          >
            <PhTrash @size="24" class="inline" />
          </Button>
        </div>
      </Header>

      <HStack>
        <VStack class="h-full md:w-2/3! md:flex-none">
          <Section>
            <:header>
              <div>{{t "tas.application.type_two.new.application"}}</div>
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
                class="font-semibold"
              >
                <:beforeTag>
                  <span>
                    {{t "common.tas.course_cash.eligible_for"}}
                  </span>
                </:beforeTag>
              </CourseCashTag>
            </:header>
            <:body>
              {{#unless this.isActiveParticipant}}
                <AlertBanner
                  @intent="warning"
                  @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                  class="my-4"
                >
                  <p class="text-sm">
                    {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                  </p>
                </AlertBanner>
              {{/unless}}
              <VStack>
                <div class="tio-row-flush text-sm">
                  <span class="tio-fg-danger text-xl">*</span>
                  {{t "tas.application.type_two.new.required_items"}}
                </div>
                <p>{{t "tas.application.type_two.new.expense_type_reminder"}}</p>
                <div class="tio-row-start-between w-full">
                  {{#if this.startDateConfig.visible}}
                    <div class="w-5/12">
                      <label for="start" class="tio-fg-secondary font-semibold">
                        {{capitalizeWords this.startDateLabel}}
                        {{#if this.startDateConfig.required}}
                          <span class="tio-fg-danger">*</span>
                        {{/if}}
                      </label>
                      <div class="relative w-full">
                        {{! @glint-expect-error: EmberFlatpickr type overrides}}
                        <EmberFlatpickr
                          @date={{or this.startDate ""}}
                          @onChange={{this.updateStartDate}}
                          @dateFormat="Y-m-d"
                          @altInput={{true}}
                          @altFormat="F j, Y"
                          @disableMobile={{true}}
                          placeholder="mm/dd/yyyy"
                          data-test="start"
                          class="tio-form-input-lg"
                        />
                        <div
                          class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                        >
                          <PhCalendarBlank @size="20" />
                        </div>
                      </div>
                      {{#let
                        (errorsForValibotField
                          "dateEntered"
                          schemaErrors=this.startDateValidationErrors
                          touched=this.touched
                        )
                        as |errors|
                      }}
                        {{#if errors}}
                          <div class="tio-fg-danger-h text-sm mt-1">
                            {{join errors ", "}}
                          </div>
                        {{/if}}
                      {{/let}}
                    </div>
                  {{/if}}
                  {{#if this.endDateConfig.visible}}
                    <div class="w-5/12">
                      <label for="end" class="tio-fg-secondary font-semibold">
                        {{capitalizeWords this.endDateLabel}}
                        {{#if this.endDateConfig.required}}
                          <span class="tio-fg-danger">*</span>
                        {{/if}}
                      </label>
                      <div class="relative w-full">
                        {{! @glint-expect-error: EmberFlatpickr type overrides}}
                        <EmberFlatpickr
                          @date={{or this.endDate ""}}
                          @onChange={{this.updateEndDate}}
                          @dateFormat="Y-m-d"
                          @altInput={{true}}
                          @altFormat="F j, Y"
                          @disableMobile={{true}}
                          placeholder="mm/dd/yyyy"
                          data-test="end"
                          class="tio-form-input-lg"
                        />
                        <div
                          class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                        >
                          <PhCalendarBlank @size="20" />
                        </div>
                      </div>
                    </div>
                  {{/if}}
                </div>
                {{#unless this.eligibility}}
                  <AlertBanner
                    @intent="warning"
                    @title={{t
                      "start_date_eligibility.errorHeader"
                      startEndSubmission=this.lockStatus
                    }}
                    data-test-eligibility-error
                    class="mb-4"
                  >
                    <p class="text-sm">
                      {{t "start_date_eligibility.errorText"}}
                    </p>
                  </AlertBanner>
                {{/unless}}

                {{#unless this.eligibleBasedOnWaitingPeriod}}
                  <AlertBanner
                    @intent="warning"
                    @title={{t "tuition_assistance.program_eligibility_warnings.dashboard_title"}}
                    class="mb-4"
                  >
                    <p class="text-sm">
                      {{t
                        "tuition_assistance.program_eligibility_warnings.invalid_start_date"
                        time=this.eligibilityWaitingPeriod
                        date=this.dateEligible
                        htmlSafe=true
                      }}
                    </p>
                  </AlertBanner>
                {{/unless}}
                {{#unless this.isActiveParticipant}}
                  <AlertBanner
                    @intent="warning"
                    @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                    class="my-4"
                  >
                    <p class="text-sm">
                      {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                    </p>
                  </AlertBanner>
                {{/unless}}
                <Divider class="my-2" />
                <div class="tio-row-flush w-full py-4">
                  <div class="w-1/2">
                    <h2 class="tio-fg-secondary font-semibold">
                      {{t "tas.application.type_two.new.name"}}
                      <span class="tio-fg-danger">*</span>
                    </h2>
                  </div>
                  <div class="w-1/2">
                    <label for="application-name" class="sr-only">
                      {{t "tas.application.type_two.new.name"}}!!
                    </label>
                    <Input
                      @size="lg"
                      data-test-application-name
                      id="application-name"
                      @value={{getSingleValueForTasField "APPLICATION_NAME" this.applicationFields}}
                      @onChange={{this.updateAppName}}
                    />
                  </div>
                </div>
                {{#if this.showGPAField}}
                  <div class="tio-row-between flex-wrap gap-y-4">
                    <label
                      for="grade-point-average"
                      class="font-semibold block mb-1 text-balance tio-fg-secondary"
                    >
                      {{t "tas.application.grade_point_average"}}
                      <span class="tio-fg-danger-h">*</span>
                    </label>
                    <Input
                      @size="lg"
                      type="number"
                      min="0"
                      max="4.0"
                      step="0.01"
                      data-test-grade-point-average
                      id="grade-point-average"
                      @value={{String
                        (or
                          (getSingleValueForTasField "GRADE_POINT_AVERAGE" this.applicationFields) 0
                        )
                      }}
                      @classes={{hash base="w-1/2"}}
                      @onChange={{this.setGPA}}
                    />
                    <Divider class="mb-4" />
                  </div>
                {{/if}}

                <TuitionAssistanceFormsCustomFieldsForProvince
                  @province="APPLICATION"
                  @customFields={{this.applicationCustomFields}}
                  @programTemplate={{this.programTemplate}}
                  @didUpdateFields={{this.didUpdateCustomFields}}
                  @formValidationErrors={{this.customFieldValidationErrors}}
                />
              </VStack>
            </:body>
          </Section>

          <Section id="education-section">
            <:header>
              <div class="tio-row-flush">
                <p>{{this.expenseTypeField}}</p>
                <span class="tio-fg-danger">*</span>
              </div>
            </:header>
            <:actions>
              <div
                class="rounded-md transition-all duration-300
                  {{if
                    (eq this.highlightedSection 'education-section')
                    'ring-4 tio-ring-brand ring-offset-2'
                  }}"
              >
                <Button
                  @intent="primary"
                  data-test-add-education-drawer
                  {{on "click" (fn this.openEducationDrawer null)}}
                  class="add-education-drawer-button"
                >
                  <PhPlus @size="24" class="mr-2" />
                  {{or this.expenseTypeField (t "tas.application.type_two.new.education")}}
                </Button>
              </div>
            </:actions>
            <:body>
              <VStack>
                <p>{{t "tas.application.type_two.new.expense_type_description"}}</p>
                <ActionableList @striped={{false}} as |l|>
                  {{#each @model.application.activeCourses as |course|}}
                    <l.Row>
                      <l.Term class="md:w-1/3">{{course.courseName}}</l.Term>
                      <l.Details>
                        <l.Item class="justify-self-center md:justify-self-start">
                          {{#if @model.application.isCreditBased}}
                            {{course.courseCredit}}
                            {{t "common.credits"}}
                          {{else}}
                            {{safeFormatNumber
                              (divide course.courseTotal 100)
                              style="currency"
                              currency="USD"
                            }}
                          {{/if}}
                        </l.Item>
                        <l.Item
                          class="justify-self-center md:justify-self-start font-semibold"
                        >{{course.courseGrade}}</l.Item>
                      </l.Details>
                      <l.Action>
                        <button
                          type="button"
                          data-test-delete-course
                          {{on "click" (fn this.toggleDeleteCourse course)}}
                        ><PhXCircle @size="24" /></button>
                        <button
                          type="button"
                          data-test-edit-education-drawer
                          {{on "click" (fn this.openEducationDrawer course)}}
                        ><PhNotePencil @size="24" /></button>
                      </l.Action>
                    </l.Row>
                  {{/each}}
                </ActionableList>
              </VStack>
            </:body>
          </Section>
          {{! Add Scholarships }}
          {{#if this.programTemplate.scholarshipsAreVisible}}
            <Section>
              <:header>
                <div class="tio-row-between">
                  <p>
                    {{this.scholarshipReceived.label}}
                    <span>{{t "tas.new.scholarships.optional"}}</span>
                  </p>
                </div>
              </:header>
              <:actions>
                <Button
                  @intent="primary"
                  data-test-add-scholarship-drawer
                  {{on "click" this.toggleScholarshipDrawer}}
                >
                  <PhPlus @size="24" class="mr-2" />
                  {{this.scholarshipReceived.label}}
                </Button>
              </:actions>
              <:body>
                <VStack>
                  <p>{{t "tas.new.scholarships.please_enter_any_scholarships"}}</p>
                  <ActionableList @striped={{false}} as |l|>
                    {{#each this.scholarships as |scholarship|}}
                      <l.Row>
                        <l.Term class="md:w-1/2">{{scholarship.scholarshipName}}</l.Term>
                        <l.Details>
                          <l.Item class="justify-self-end md:justify-self-start">
                            {{safeFormatNumber
                              (divide scholarship.scholarshipAmount 100)
                              style="currency"
                              currency="USD"
                            }}
                          </l.Item>
                        </l.Details>
                        <l.Action>
                          <button type="button" data-test-delete-scholarship>
                            <PhXCircle
                              @size="24"
                              class="tio-fg-secondary"
                              {{on "click" (fn this.openDeleteScholarship scholarship)}}
                            />
                          </button>
                          <button type="button" data-test-edit-scholarship>
                            <PhNotePencil
                              @size="24"
                              class="tio-fg-secondary"
                              {{on "click" (fn this.openEditScholarship scholarship)}}
                            />
                          </button>
                        </l.Action>
                      </l.Row>
                    {{/each}}
                  </ActionableList>
                </VStack>
              </:body>
            </Section>
          {{/if}}
          {{! Upload Attachments}}
          {{#if this.shouldShowAttachments}}
            <Section id="attachments-section">
              <:header>
                {{t "common.attachments"}}
                {{#if this.isAttachmentRequired}}
                  <span class="tio-fg-danger text-xl">*</span>
                {{/if}}
              </:header>
              <:body>
                <VStack>
                  <div
                    class="transition-all duration-300 rounded-lg
                      {{if
                        (eq this.highlightedSection 'attachments-section')
                        'ring-2 tio-ring-brand ring-offset-2'
                      }}"
                  >
                    <UploadAttachments
                      @programTemplate={{this.programTemplate}}
                      @classification={{this.attachmentClassificationString}}
                      @attachable={{@model.application}}
                      @attachableType="tasApplication"
                      @useDropzone={{true}}
                      @items={{this.attachmentsListItems}}
                      @instructions={{this.attachmentsListInstructions}}
                      @hideDiscarded={{true}}
                      {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                    />
                  </div>
                  {{#if this.attachmentsListItems.length}}
                    <div
                      class="mt-3 rounded-md tio-bg-info-soft border tio-border-info p-3 text-sm tio-fg-info tio-row-top-tight"
                    >
                      <PhLightbulb @size={{18}} class="shrink-0 mt-0.5" />
                      <p>
                        {{t "tas.guided.typed_attachments_note"}}
                        <button
                          type="button"
                          class="ml-1 font-medium underline hover:tio-fg-info-body"
                          disabled={{this.switchToGuidedFlow.isRunning}}
                          {{on "click" this.switchToGuidedFlow.perform}}
                        >
                          {{t "tas.guided.try_guided_view"}}
                        </button>
                      </p>
                    </div>
                  {{/if}}
                </VStack>
              </:body>
            </Section>
          {{/if}}
        </VStack>
        <VStack class="w-full md:w-1/3! md:flex-none">
          <Section>
            <:header>{{t "tas.benefits_summary.title"}}</:header>
            <:body>
              <LimitsSummary
                @application={{this.application}}
                @employee={{@model.programInstance.employee}}
                @labelClass="col-span-5"
                @valueClass="col-span-2 tio-fg-brand"
                @paymentMethod={{this.paymentMethod}}
                @shouldFetchLimits={{this.shouldFetchLimits}}
                class="my-4"
                as |Summary|
              >
                <div class="w-full grid grid-cols-7 gap-2 mt-3">
                  {{#unless Summary.isUnlimitedMaximumAnnualBenefit}}
                    <Summary.MaxAnnualBenefitLineItem />
                  {{/unless}}
                  {{#if Summary.hasLimitsErrorForEmployee}}
                    <Summary.RemainingBenefitLineItem />
                  {{/if}}
                  <Summary.ApplicationAmountRequestedWithoutScholarshipsLineItem
                    @valueClass="tio-fg-primary col-span-2"
                  />
                  {{#if (and this.programTemplate.scholarshipsAreVisible (not Summary.isCredits))}}
                    <Summary.ScholarshipAmountLineItem
                      @valueClass="tio-fg-primary !font-normal col-span-2"
                    />
                  {{/if}}
                  <Summary.LimitsAlertForEmployeeIfApplicable class="text-sm col-span-full" />
                  <ReimbursementCalculationIfApplicable @application={{@model.application}} />
                  <Divider class="my-2 col-span-7" />

                  {{#if this.programTemplate.isDirectPayProgram}}
                    <div class="col-span-7 tio-row-between">
                      <p>{{t "tas.payment_method.default"}}</p>
                      <Button
                        {{on "click" (fn (mut this.showPaymentMethodModal) true)}}
                        @appearance="minimal"
                      >
                        <PhPencil @size="20" />
                      </Button>
                    </div>
                    <Summary.ApplicationCourseCashSubtotalLineItem
                      @labelClass="ml-6 col-span-5"
                      @valueClass="tio-fg-primary !font-normal col-span-2"
                    >
                      <CourseCashTag @isDirectPayProgram={{true}} @size="sm" />
                    </Summary.ApplicationCourseCashSubtotalLineItem>
                    <Summary.ApplicationAllOtherExpensesSubtotalLineItem
                      @labelClass="ml-6 col-span-5"
                      @valueClass="tio-fg-primary !font-normal col-span-2"
                    >
                      <ReimbursementPopoverTag @size="sm" />
                    </Summary.ApplicationAllOtherExpensesSubtotalLineItem>
                    <Divider class="my-2 col-span-7" />
                  {{/if}}

                  {{#if this.programTemplate.calculateTotalByGPA}}
                    <Summary.ApplicationAmountBasedOnGPALineItem />
                  {{else}}
                    <Summary.ApplicationAmountRequestedLineItem />
                  {{/if}}
                </div>

                {{! Required Items Section -ASIDE }}
                <section class="my-5">
                  <header
                    class="border-y tio-border-default bg-tio-gray-25 tio-row-tight py-2 px-5 tio-text-sm-medium tracking-wide -mx-4"
                  >
                    <PhClipboardText @size="32" />
                    <span>{{t "tas.benefits_summary.required_items"}}</span>
                  </header>
                  <button
                    type="button"
                    class="tio-row mt-4 cursor-pointer group bg-transparent border-0 p-0 w-full text-left"
                    aria-label={{this.expenseTypeField}}
                    {{on "click" (fn this.scrollTo "education-section")}}
                  >
                    {{#if @model.application.activeCourses.length}}
                      <PhCheckCircle @size="24" @weight="fill" class="tio-fg-brand shrink-0" />
                    {{else}}
                      <PhCircle
                        @size="24"
                        class="tio-fg-faint group-hover:tio-fg-brand shrink-0 transition-colors"
                      />
                    {{/if}}
                    <span
                      class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                    >{{this.expenseTypeField}}</span>
                  </button>
                  {{#if this.isAttachmentRequired}}
                    <div>
                      <div class="tio-row-between flex-row">
                        <button
                          type="button"
                          class="tio-row cursor-pointer group bg-transparent border-0 p-0 text-left"
                          aria-label={{t "tas.benefits_summary.attachments"}}
                          {{on "click" (fn this.scrollTo "attachments-section")}}
                        >
                          {{#if this.hasRequiredAttachments}}
                            <PhCheckCircle
                              @size="24"
                              @weight="fill"
                              class="tio-fg-brand shrink-0"
                            />
                          {{else}}
                            <PhCircle
                              @size="24"
                              class="tio-fg-faint group-hover:tio-fg-brand shrink-0 transition-colors"
                            />
                          {{/if}}
                          <span
                            class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                          >{{t "tas.benefits_summary.attachments"}}</span>
                        </button>
                        <div>
                          <UploadAttachments
                            @programTemplate={{this.programTemplate}}
                            @classification={{this.attachmentClassificationString}}
                            @attachable={{@model.application}}
                            @attachableType="tasApplication"
                            @useDropzone={{false}}
                            @iconOnlyUpload={{true}}
                            @hideInstructions={{true}}
                            @hideAttachments={{true}}
                            {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                          />
                        </div>
                      </div>
                      {{#if this.attachmentsListItems.length}}
                        <div class="tio-row -mt-1">
                          {{#if this.hasRequiredAttachments}}
                            <PhCheckCircle @size="24" class="tio-fg-brand shrink-0" />
                          {{else}}
                            <PhCircle @size="24" class="tio-fg-faint shrink-0" />
                          {{/if}}
                          <p>{{join this.attachmentsListItems ", "}}</p>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}
                  {{#if this.conditionsForSubmit.length}}
                    <AcceptConditions
                      @conditions={{this.conditionsForSubmit}}
                      @onChange={{this.didUpdateAcceptedConditions}}
                    />
                  {{/if}}
                </section>

                <div class="py-4">
                  <Button
                    @intent="primary"
                    @isRunning={{this.submitTask.isRunning}}
                    class="tio-flex-center w-full py-3 px-6 me-2 mb-2 font-medium text-lg tio-fg-inverse focus:outline-none tio-bg-info rounded-lg hover:tio-bg-info-hover"
                    @isDisabled={{or
                      Summary.isDisabledSubmitForEmployee
                      (not this.canSubmitApplication)
                    }}
                    data-test-submit-application
                    {{on "click" this.submitApplication}}
                  >
                    {{t "tas.benefits_summary.submit_application"}}
                  </Button>
                  <p class="tio-text-sm-light text-center">
                    {{t "tas.benefits_summary.helper_text"}}
                  </p>
                  {{#if this.submitError}}
                    <ErrorMessages
                      @showErrors={{true}}
                      @error={{this.submitError}}
                      class="text-sm"
                    />
                  {{/if}}
                </div>
              </LimitsSummary>
            </:body>
          </Section>
        </VStack>
      </HStack>
    </VStack>
    {{#if this.isEducationOpen}}
      <AddEducationDrawer
        @isOpen={{this.isEducationOpen}}
        @onClose={{this.closeEducationDrawer}}
        @application={{@model.application}}
        @course={{this.course}}
        @header={{this.expenseTypeField}}
        @paymentMethod={{this.paymentMethod}}
        @setPaymentMethod={{this.setPaymentMethod}}
      />
    {{/if}}
    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipOpen}}
      @onClose={{this.toggleScholarshipDrawer}}
      @application={{@model.application}}
      @scholarshipIdToEdit={{this.scholarshipIdToEdit}}
    />

    {{! Delete Application Modal }}
    <Modal
      @isOpen={{this.isDeleteApplicationOpen}}
      @onClose={{this.toggleDeleteApplication}}
      data-test-delete-application-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_application"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_application_delete"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteApplication.isRunning}}
          @onCancel={{this.toggleDeleteApplication}}
          @onSubmit={{this.deleteApplication.perform}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isDeleteCourseOpen}}
      @onClose={{fn this.toggleDeleteCourse null}}
      data-test-delete-course-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_course"}}</:header>
      <:body>
        <p>{{t "tas.application.type_two.new.confirm_delete_expense"}}:</p>
        <p class="font-semibold px-2">{{this.course.courseName}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteCourse.isRunning}}
          @onCancel={{fn this.toggleDeleteCourse null}}
          @onSubmit={{this.deleteCourse.perform}}
        />
      </:footer>
    </Modal>
    {{! Delete Scholarship Modal }}
    <Modal
      @isOpen={{this.isDeleteScholarshipOpen}}
      @onClose={{this.closeDeleteScholarship}}
      data-test-delete-scholarship-modal
    >
      <:header>{{t "tas.application.type_two.new.delete_scholarship"}}</:header>
      <:body>
        {{#let this.scholarshipToDelete as |scholarship|}}
          {{#if scholarship}}
            <p>{{t "tas.application.type_two.new.confirm_scholarship_delete"}}</p>
            <div class="p-8 tio-flex-between w-2/3">
              <p class="font-medium">{{scholarship.scholarshipName}}</p>
              <p class="font-medium">
                {{safeFormatNumber
                  (divide scholarship.scholarshipAmount 100)
                  style="currency"
                  currency="USD"
                }}
              </p>
            </div>
          {{/if}}
        {{/let}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteScholarship}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deleteScholarship}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>

    <PaymentMethodAndSubtotals
      @show={{this.showPaymentMethodModal}}
      @cancel={{this.closePaymentMethodModal}}
      @application={{@model.application}}
      @paymentMethod={{this.paymentMethod}}
      @setPaymentMethod={{this.setPaymentMethod}}
    />
  </template>
}
